import { useEffect, useMemo, useRef } from 'react'
import { useAppStore } from '@core/store'
import type { LoadedModel } from '@viewer/store/viewerSlice'
import { forgetModelSuggestions } from './lib/idsSuggest'
import { idsModelKey } from './store/idsModuleSlice'

// Modelos do módulo IDS, num sítio só: o modelo efectivo a validar e a
// invalidação do que está em memória quando os IFCs carregados mudam.
// Usado pelos dois motores (useIdsValidation, para o IDS standard, e
// useIdsxRun, para a extensão .idsx), porque a pergunta "estes
// resultados ainda são deste modelo?" é a mesma nos dois.

/**
 * @param sel Escolha de modelo do utilizador (pode já não existir).
 * @returns Os modelos carregados e o modelo efectivo: a escolha se ainda
 *   for válida, senão o activo, senão o primeiro.
 */
export function useIdsModels(sel: string): {
  models: LoadedModel[]
  modelId: string
} {
  const models = useAppStore((s) => s.models)
  const activeModelId = useAppStore((s) => s.activeModelId)
  const storedKey = useAppStore((s) => s.idsModelKey)
  const syncModels = useAppStore((s) => s.syncIdsModels)

  const key = useMemo(() => idsModelKey(models), [models])
  const seenRef = useRef<Set<string>>(new Set())

  // Os IFCs mudaram (trocados, descarregados, sessão fechada): o que
  // está em memória descreve outro modelo e vai abaixo, com aviso. Não
  // há limpeza no desmontar - o estado é do store, não da página.
  useEffect(() => {
    const live = new Set(models.map((m) => m.id))
    // A cache de módulo do idsSuggest é a segunda cópia das sugestões:
    // sem isto ficava a segurar os psets de modelos já descarregados.
    // Fora da guarda de chave de propósito, para apanhar também o
    // "Fechar sessão", que já limpou o slice antes de largar os IFCs.
    for (const id of seenRef.current) {
      if (!live.has(id)) forgetModelSuggestions(id)
    }
    seenRef.current = live
    if (key === storedKey) return
    syncModels(key, [...live])
  }, [key, storedKey, models, syncModels])

  const modelId = models.some((m) => m.id === sel)
    ? sel
    : (activeModelId ?? models[0]?.id ?? '')

  return { models, modelId }
}
